import { useCreateChoiceMutation } from '@/modules/services';
import { Choice, ChoiceMetadataType } from '@/modules/types/choice';
import { QuizQuestion } from '@/modules/types/question';
import { AddIcon, Button, Col, Row } from 'elmo-elements';
import React, { ReactElement } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import ChoiceGroup from './components/choiceGroup';
import DragGroup from './components/dragGroup';

interface MultiChoiceProps {
  type: 'radio' | 'checkbox' | 'order';
}

export const MultiChoice = ({ type }: MultiChoiceProps): ReactElement => {
  const { control, getValues } = useFormContext<QuizQuestion>();
  const { remove, move, fields, update, replace, append } = useFieldArray({
    name: 'choices',
    control,
  });

  const [createChoice, createChoiceStatus] = useCreateChoiceMutation();

  const addChoice = () => {
    append({
      content: '',
      position: getValues('choices')?.length || fields.length,
      isCorrect: false,
      type: ChoiceMetadataType.main,
    });
  };

  const revalidatePosition = () => {
    const newChoices =
      getValues('choices')?.map((choice, index) => {
        const newValue: Choice = { ...choice };
        newValue.position = index;
        newValue.type = ChoiceMetadataType.main;

        return newValue;
      }) || [];

    const result = newChoices.sort(
      (first, second) => first.position - second.position,
    );

    replace(result);
  };

  const removeChoice = (choice: Choice, index: number) => {
    remove(index);
    revalidatePosition();
  };

  return (
    <Row>
      <Col xs={24}>
        {type === 'order' ? (
          <DragGroup
            choices={fields || getValues('choices')}
            hasDelete
            onDelete={removeChoice}
            onDragEnd={(result) => {
              if (result.destination) {
                move(result.source.index, result.destination.index);
                revalidatePosition();
              }
            }}
            control={control}
          />
        ) : (
          <ChoiceGroup
            type={type}
            choices={fields || getValues('choices')}
            hasDelete
            onDelete={removeChoice}
          />
        )}
      </Col>
      <Col xs={24}>
        <Button
          onClick={() => {
            addChoice();
          }}
          isLoading={createChoiceStatus.isLoading}
          icon={<AddIcon />}
          isUppercase={false}
        >
          Add choice
        </Button>
      </Col>
    </Row>
  );
};

export default MultiChoice;
